import { z } from 'zod';

/**
 * Schemas for the `script` flow step type — a user-defined snippet of
 * TypeScript / Python / Java that produces typed output variables for
 * downstream steps to consume.
 *
 * `FlowStep.fields` in the editor model is a flat `Record<string, string>`
 * (see packages/flow-builder/src/types/flow-types.ts). Rather than
 * widening that core shape across the codebase, the structured outputs
 * declaration is JSON-encoded into the single `outputs` field. Helpers
 * `parseScriptOutputs` / `serializeScriptOutputs` round-trip the value
 * safely on both sides of the wire.
 */

export const ScriptLanguageSchema = z.enum(['typescript', 'python', 'java']);
export type ScriptLanguage = z.infer<typeof ScriptLanguageSchema>;

export const ScriptOutputTypeSchema = z.enum(['string', 'number', 'boolean', 'array', 'object', 'date', 'auto']);
export type ScriptOutputType = z.infer<typeof ScriptOutputTypeSchema>;

export const ScriptOutputScopeSchema = z.enum(['flow', 'project']);
export type ScriptOutputScope = z.infer<typeof ScriptOutputScopeSchema>;

export const ScriptOutputSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/),
  type: ScriptOutputTypeSchema.default('auto'),
  scope: ScriptOutputScopeSchema.default('flow'),
});
export type ScriptOutput = z.infer<typeof ScriptOutputSchema>;

export const ScriptOutputsArraySchema = z.array(ScriptOutputSchema);

export const ScriptStepFieldsSchema = z.object({
  language: ScriptLanguageSchema,
  code: z.string().max(100_000),
  outputs: z.string(),
  timeout: z.string().optional(),
});
export type ScriptStepFields = z.infer<typeof ScriptStepFieldsSchema>;

export const DEFAULT_SCRIPT_TIMEOUT_MS = 5000;
export const MAX_SCRIPT_TIMEOUT_MS = 60_000;

export const SCRIPT_OUTPUT_UPDATE_TYPES = ['flow', 'project'] as const;

/**
 * Wire shape used by the runner -> flow-builder step-complete payload
 * to forward script output variables that should be persisted at the
 * project scope. Values are JSON-encoded when not strings; the editor
 * stores them in `EnvironmentConfig.projectVariables` verbatim.
 */
export const ScriptOutputUpdateSchema = z.object({
  name: z.string(),
  value: z.string(),
  scope: ScriptOutputScopeSchema,
  type: ScriptOutputTypeSchema.optional(),
});
export type ScriptOutputUpdate = z.infer<typeof ScriptOutputUpdateSchema>;

export function parseScriptOutputs(raw: string | undefined | null): ScriptOutput[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    const result = ScriptOutputsArraySchema.safeParse(parsed);
    return result.success ? result.data : [];
  } catch {
    return [];
  }
}

export function serializeScriptOutputs(outputs: ScriptOutput[]): string {
  return JSON.stringify(outputs);
}
